import { ImageIcon } from "lucide-react"
import { useMemo, useState, type ReactNode } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { CatalogPickerDialog, catalogPickerCardClassName } from "@/components/ui/catalog-picker-dialog"
import { useTranslation } from "@/hooks/use-translation"
import {
  LOCATION_FACINGS,
  PRIMARY_LOCATION_FACING,
  type LocationFacing,
  type LocationViewpointRow,
  locationViewpointDownloadUrl,
} from "@/lib/locations-api"

export type LocationAngleFacingItem = {
  facing: LocationFacing
  url: string | null
  thumbUrl: string | null
  label: string
}

type LocationAnglesPickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  facings: LocationAngleFacingItem[]
  viewpoints: LocationViewpointRow[]
  onSelectFacing?: (facing: LocationFacing) => void
  onSelectViewpoint?: (viewpoint: LocationViewpointRow) => void
  /** Override section labels (shot UI uses “photographed”). */
  basicSectionLabel?: string
  additionalSectionLabel?: string
}

type LightboxTarget = {
  url: string
  title: string
}

function matchesQuery(label: string, query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return label.toLowerCase().includes(q)
}

function viewpointDisplayLabel(
  vp: LocationViewpointRow,
  all: LocationViewpointRow[],
  labelForIndex: (index: number) => string,
): string {
  const named = vp.name?.trim()
  if (named) return named
  const idx = all.findIndex((v) => v.id === vp.id)
  return labelForIndex(idx >= 0 ? idx + 1 : 1)
}

export function LocationAnglesPickerDialog({
  open,
  onOpenChange,
  facings,
  viewpoints,
  onSelectFacing,
  onSelectViewpoint,
  basicSectionLabel,
  additionalSectionLabel,
}: LocationAnglesPickerDialogProps) {
  const { t } = useTranslation(["workspace"])
  const [filterText, setFilterText] = useState("")
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null)

  const filteredFacings = useMemo(
    () => facings.filter((item) => matchesQuery(item.label, filterText)),
    [facings, filterText],
  )

  const filteredViewpoints = useMemo(() => {
    const q = filterText.trim().toLowerCase()
    if (!q) return viewpoints
    const additionalLabel = (additionalSectionLabel ?? t("locationsPage.angles.sections.additional")).toLowerCase()
    const sectionLabel = t("locationsPage.angles.sections.additional").toLowerCase()
    const photographed = t("locationsPage.angles.additionalItem").toLowerCase()
    if (additionalLabel.includes(q) || sectionLabel.includes(q) || photographed.includes(q)) {
      return viewpoints
    }
    const labelForIndex = (index: number) => t("locationsPage.angles.additionalItemIndexed", { index })
    return viewpoints.filter((vp) => matchesQuery(viewpointDisplayLabel(vp, viewpoints, labelForIndex), filterText))
  }, [viewpoints, filterText, t, additionalSectionLabel])

  const sections = useMemo(() => {
    const out: { id: string; label: string; items: ReactNode }[] = []

    if (filteredFacings.length > 0) {
      out.push({
        id: "basic",
        label: basicSectionLabel ?? t("locationsPage.angles.sections.basic"),
        items: filteredFacings.map((item) => {
          return (
            <button
              key={item.facing}
              type="button"
              className={catalogPickerCardClassName(false)}
              onClick={() => {
                if (onSelectFacing) {
                  onSelectFacing(item.facing)
                  onOpenChange(false)
                  return
                }
                if (!item.url) return
                setLightbox({ url: item.url, title: item.label })
              }}
            >
              <div className="aspect-video overflow-hidden rounded-lg bg-ui-surface-sunken">
                {item.url ? (
                  <img src={item.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-ui-text-muted">
                    <ImageIcon className="h-5 w-5 opacity-60" />
                    <span className="text-center text-[10px] leading-tight">{item.label}</span>
                  </div>
                )}
              </div>
              <p className="mt-2 line-clamp-2 text-xs font-medium text-ui-text-primary">{item.label}</p>
            </button>
          )
        }),
      })
    }

    if (filteredViewpoints.length > 0) {
      out.push({
        id: "additional",
        label: additionalSectionLabel ?? t("locationsPage.angles.sections.additional"),
        items: filteredViewpoints.map((vp) => {
          const title = viewpointDisplayLabel(vp, viewpoints, (index) =>
            t("locationsPage.angles.additionalItemIndexed", { index }),
          )
          return (
            <button
              key={vp.id}
              type="button"
              className={catalogPickerCardClassName(false)}
              onClick={() => {
                if (onSelectViewpoint) {
                  onSelectViewpoint(vp)
                  onOpenChange(false)
                  return
                }
                const sourceUrl = locationViewpointDownloadUrl(vp)
                if (!sourceUrl) return
                setLightbox({ url: sourceUrl, title })
              }}
            >
              <div className="aspect-video overflow-hidden rounded-lg bg-ui-surface-sunken">
                {vp.url ? (
                  <img src={vp.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-ui-text-muted">
                    <ImageIcon className="h-5 w-5 opacity-60" />
                    <span className="text-center text-[10px] leading-tight">{title}</span>
                  </div>
                )}
              </div>
              <p className="mt-2 line-clamp-2 text-xs font-medium text-ui-text-primary">{title}</p>
            </button>
          )
        }),
      })
    }

    return out
  }, [
    filteredFacings,
    filteredViewpoints,
    viewpoints,
    t,
    onSelectFacing,
    onSelectViewpoint,
    onOpenChange,
    basicSectionLabel,
    additionalSectionLabel,
  ])

  const isEmpty = filteredFacings.length === 0 && filteredViewpoints.length === 0

  return (
    <>
      <CatalogPickerDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setFilterText("")
            setLightbox(null)
          }
          onOpenChange(next)
        }}
        title={t("locationsPage.angles.pickerTitle")}
        closeLabel={t("locationsPage.roomViewer.close")}
        searchValue={filterText}
        onSearchChange={setFilterText}
        searchPlaceholder={t("locationsPage.angles.searchPlaceholder")}
        sections={sections}
        emptyLabel={t("locationsPage.angles.noResults")}
        isEmpty={isEmpty}
      />
      <ImageLightboxDialog
        open={lightbox != null}
        onOpenChange={(next) => {
          if (!next) setLightbox(null)
        }}
        url={lightbox?.url ?? null}
        title={lightbox?.title ?? null}
        downloadUrl={lightbox?.url ?? null}
        downloadFilename={lightbox?.title ? `${lightbox.title}.png` : null}
        copyable={Boolean(lightbox?.url)}
      />
    </>
  )
}

export function buildLocationAngleFacingItems(
  urls: Partial<Record<LocationFacing, string | null>> | undefined,
  thumbs: Partial<Record<LocationFacing, string | null>> | undefined,
  labelFor: (facing: LocationFacing) => string,
  primaryFallback?: string | null,
): LocationAngleFacingItem[] {
  return LOCATION_FACINGS.map((facing) => {
    const url = urls?.[facing] ?? (facing === PRIMARY_LOCATION_FACING ? (primaryFallback ?? null) : null)
    const thumbUrl = thumbs?.[facing] ?? url
    return {
      facing,
      url: url ?? null,
      thumbUrl: thumbUrl ?? null,
      label: labelFor(facing),
    }
  })
}
